import { useEffect, useRef, useState } from "react";
import { useCreateReportTemplatePreviewUploadMutation } from "~/graphql/supergraphHooks";
import { dataURItoBlob, getContentTypeFromExtension } from "../utils";
import type { TemplateFileData } from "../components/TemplateCreation";

const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;
const DEBOUNCE_MS = 500;

type UseTemplatePreviewUploadParams = {
  templateFile: TemplateFileData | null;
  /** DB id of the arena the preview is scoped to. */
  arenaId: number | null;
  templateName: string;
  skip?: boolean;
};

/**
 * Uploads the in-progress docx to a scoped preview prefix and returns its S3 key
 * so the draft template can be rendered through the async preview workflow
 * (there is no persisted template row yet). Debounced 500ms so rapid edits don't
 * spam S3, and superseded uploads are discarded.
 */
export const useTemplatePreviewUpload = ({
  templateFile,
  arenaId,
  templateName,
  skip = false,
}: UseTemplatePreviewUploadParams) => {
  const [templateS3Key, setTemplateS3Key] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [createPreviewUpload] = useCreateReportTemplatePreviewUploadMutation();

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic id so a slow upload that finishes after a newer edit is discarded.
  const requestIdRef = useRef(0);

  const dataURI = templateFile?.dataURI ?? null;
  const fileType = templateFile?.fileType ?? "";

  useEffect(() => {
    const cleanup = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    if (!dataURI || arenaId == null || skip) {
      cleanup();
      requestIdRef.current += 1;
      setTemplateS3Key(null);
      setIsUploading(false);
      setUploadError(null);
      return cleanup;
    }

    cleanup();

    timeoutRef.current = setTimeout(() => {
      const requestId = (requestIdRef.current += 1);
      setIsUploading(true);
      setUploadError(null);
      setTemplateS3Key(null);

      void (async () => {
        try {
          const blob = dataURItoBlob(dataURI);
          const contentType = getContentTypeFromExtension(fileType);
          const fileName = `${templateName || "template"}.${fileType}`;

          const result = await createPreviewUpload({
            variables: {
              input: { arenaId, fileName, contentType, fileSize: blob.size },
            },
          });

          const uploadData = result.data?.createReportTemplatePreviewUpload;
          if (!uploadData?.uploadUrl || !uploadData?.s3Key) {
            throw new Error("Failed to prepare preview upload");
          }

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
          try {
            const uploadResponse = await fetch(uploadData.uploadUrl, {
              method: "PUT",
              body: blob,
              signal: controller.signal,
              headers: { "Content-Type": contentType },
            });
            if (!uploadResponse.ok) {
              throw new Error(`Failed to upload preview (${uploadResponse.status})`);
            }
          } finally {
            clearTimeout(timeoutId);
          }

          if (requestId !== requestIdRef.current) return;
          setTemplateS3Key(uploadData.s3Key);
          setIsUploading(false);
        } catch {
          if (requestId !== requestIdRef.current) return;
          setUploadError("Failed to prepare preview");
          setIsUploading(false);
        }
      })();
    }, DEBOUNCE_MS);

    return cleanup;
  }, [dataURI, fileType, arenaId, templateName, skip, createPreviewUpload]);

  return { templateS3Key, isUploading, uploadError };
};
